/**
 * Hand-drawn 1-bit creature glyphs.
 *
 * Dwarf Fortress reads because a creature's *silhouette* carries its identity
 * before colour does. So none of these trace a font. Each is a drawn shape with
 * a distinct outline: the grunt is a squat blob, the runner is a thin diagonal,
 * the armoured is a full-width brick, the weaver is an S, the splitter is a
 * seamed circle, the shielded sits between two bars. Squint at any two and they
 * stay apart.
 *
 * Encoding, one character per pixel:
 *   `.`  empty
 *   `#`  solid fill      → atlas R channel
 *   `+`  detail / hot    → atlas B channel (eyes, seams, cores, embers)
 *   `-`  dark core       → atlas G channel, authored rather than derived
 *
 * The outline (atlas G) is normally not authored here. `GlyphAtlas` derives it
 * by dilating the drawn mass by one pixel, which guarantees an unbroken dark
 * rim on every glyph without anyone having to trace it by hand.
 *
 * `-` writes that same dark channel directly, which is what a *hollow* body
 * needs. Dilation alone cannot fill the inside of a ring wider than two pixels,
 * and a ring with a transparent middle lets the lattice shine through the body.
 * Only the swarmling uses it today; see the note above that family.
 *
 * Nothing in this file imports Three.js. It is plain data, safe to load in a
 * test or a worker.
 */

import type { EnemyFamily } from '@/core/types';

export interface GlyphDef {
  /** Edge length in pixels. Every glyph is square. */
  readonly size: number;
  /** `size` strings of `size` characters, top row first. */
  readonly rows: readonly string[];
}

// ────────────────────────────────────────────────────────── creatures (16) ──

/** Squat humanoid. Heavy shoulders, stubby legs, low centre of mass. */
const grunt: GlyphDef = {
  size: 16,
  rows: [
    '................',
    '......####......',
    '.....######.....',
    '.....#+##+#.....',
    '.....######.....',
    '......####......',
    '...##########...',
    '..############..',
    '..###.####.###..',
    '..##..####..##..',
    '......####......',
    '.....######.....',
    '.....##..##.....',
    '....###..###....',
    '....###..###....',
    '...####..####...',
  ],
};

/*
 * ── grunt tiers ──
 *
 * Axis: **how much plate is bolted to the shoulders**, plus overall mass.
 *
 * `heavy` grows square pauldrons that jut past the torso, so the widest point
 * moves from the hips to the shoulders. `elite` keeps those and adds a two-horn
 * crown, which is the only tier marker in this family that changes the *top*
 * edge — the fastest thing to read in a corridor where bodies walk left to
 * right. `monolith` throws the humanoid reading away: no neck, no arm gap, one
 * slab over a plinth.
 */

/** HEAVY. Pauldrons past the torso; the widest point is now the shoulders. */
const gruntHeavy: GlyphDef = {
  size: 18,
  rows: [
    '..................',
    '.....########.....',
    '....##########....',
    '....#+######+#....',
    '....##########....',
    '.....########.....',
    '###..########..###',
    '###..########..###',
    '###..########..###',
    '.##..########..##.',
    '.....########.....',
    '.....########.....',
    '.....###..###.....',
    '.....###..###.....',
    '....####..####....',
    '....####..####....',
    '...#####..#####...',
    '..................',
  ],
};

/** ELITE. The crown is the tell: two horns break the top edge. */
const gruntElite: GlyphDef = {
  size: 20,
  rows: [
    '..##............##..',
    '..###..........###..',
    '...####......####...',
    '......########......',
    '.....##########.....',
    '.....#+######+#.....',
    '.....##########.....',
    '......########......',
    '####..########..####',
    '####.##########.####',
    '####.####++####.####',
    '####.####++####.####',
    '.###.##########.###.',
    '......########......',
    '......########......',
    '......###..###......',
    '.....####..####.....',
    '.....####..####.....',
    '....#####..#####....',
    '....................',
  ],
};

/**
 * MONOLITH. 320hp and armour 4, and it stops being a humanoid to say so.
 *
 * Drawn at 24 rather than 16 because the draw size *is* the art size —
 * `EnemyRenderer` sends the glyph's padded rect as the quad extent and never
 * looks at `archetype.radius`. The catalogue has said this thing is twice the
 * grunt's size since it was written (radius 10 against 5) and the screen has
 * never once shown it. A 26px quad against the base grunt's 18 is the tier
 * marker that survives furthest into the distance, before any of the drawing
 * inside it resolves at all.
 */
const gruntMonolith: GlyphDef = {
  size: 24,
  rows: [
    '........................',
    '....################....',
    '...##################...',
    '..####################..',
    '..####################..',
    '..###++##########++###..',
    '..###++##########++###..',
    '..####################..',
    '########################',
    '########################',
    '########################',
    '####..############..####',
    '####..############..####',
    '########################',
    '..####################..',
    '..####################..',
    '..####################..',
    '...######......######...',
    '...######......######...',
    '..########....########..',
    '..########....########..',
    '.##########..##########.',
    '.##########..##########.',
    '........................',
  ],
};

/** Sprinter mid-stride, leaning hard into the run. Reads as a thin diagonal. */
const runner: GlyphDef = {
  size: 16,
  rows: [
    '................',
    '..........####..',
    '.........######.',
    '.........#+##+#.',
    '.........######.',
    '..##.....#####..',
    '...###..#####...',
    '.....########...',
    '....########....',
    '...#####+##.....',
    '...####..##.....',
    '..####....##....',
    '.####......##...',
    '.###........##..',
    '..#..........##.',
    '.............##.',
  ],
};

/*
 * ── runner tiers ──
 *
 * Axis: **what the body does with the lean**. The base is one unbroken
 * diagonal, so each tier breaks it a different way — `blink` cuts it into three
 * floating chunks, `courier` fattens the upper half into a load, `vector` grows
 * a comb of fins off the trailing edge. Nothing here changes the diagonal
 * itself, because the diagonal is what says *runner* rather than *grunt*.
 */

/**
 * BLINK. Three chunks on the diagonal with two rows of nothing between them.
 *
 * The gaps are two rows, not one. At 9.67 CSS px a 16px glyph gives 0.54 CSS px
 * per art pixel, so a one-row gap is half a screen pixel and the bloom closes
 * it — the body would read as solid and the tier marker would not exist.
 */
const runnerBlink: GlyphDef = {
  size: 16,
  rows: [
    '................',
    '..........####..',
    '.........######.',
    '.........#+##+#.',
    '.........######.',
    '..........####..',
    '................',
    '................',
    '.....######.....',
    '....########....',
    '....########....',
    '.....######.....',
    '................',
    '................',
    '.####...........',
    '.####...........',
  ],
};

/** COURIER. 240hp of cargo: the upper half swells into one carried mass. */
const runnerCourier: GlyphDef = {
  size: 20,
  rows: [
    '....................',
    '.............#####..',
    '............#######.',
    '............#+###+#.',
    '............#######.',
    '.............#####..',
    '....########.#####..',
    '...##############...',
    '...##############...',
    '....############....',
    '.....##########.....',
    '....########........',
    '...####..###........',
    '..####.....##.......',
    '.####.......##......',
    '.###.........##.....',
    '..#...........##....',
    '...............##...',
    '................##..',
    '....................',
  ],
};

/** VECTOR. 140px/s, and the trailing edge grows a comb of three swept fins. */
const runnerVector: GlyphDef = {
  size: 22,
  rows: [
    '......................',
    '..............######..',
    '.............########.',
    '.............#+####+#.',
    '.............########.',
    '..............######..',
    '..........##########..',
    '.........##########...',
    '........##########....',
    '.......##########.....',
    '......##########......',
    '.....##########.......',
    '....##########........',
    '###.#########.........',
    '.###.########.........',
    '###..#######..........',
    '.###..######..........',
    '###...#####...........',
    '.###...####...........',
    '.......###............',
    '.......##.............',
    '......................',
  ],
};

/** Plated brick. Fills the cell edge to edge, visor slit glowing. */
const armoured: GlyphDef = {
  size: 16,
  rows: [
    '................',
    '....########....',
    '...##########...',
    '...#++####++#...',
    '...##########...',
    '..############..',
    '.##############.',
    '################',
    '##+##########+##',
    '##+##########+##',
    '.##############.',
    '..############..',
    '...##########...',
    '...###....###...',
    '...###....###...',
    '..####....####..',
  ],
};

/*
 * ── armoured tiers ──
 *
 * Axis: **the top edge**, and the roster's cleanest size ladder — 18, 20, 22,
 * 24 against radii of 6, 7, 9 and 11. The base is a smooth dome; `bulwark`
 * grows a keel to a point; `siege` detaches two shoulder blocks so the profile
 * gains a notch either side; `citadel` crenellates. Four different answers to
 * "what is the outline of the roof", which is the part of a brick you can still
 * see when the brick is nine pixels wide.
 */

/** BULWARK. A keel, rising to a point where the base is flat. */
const armouredBulwark: GlyphDef = {
  size: 20,
  rows: [
    '....................',
    '........####........',
    '.......######.......',
    '.....##########.....',
    '....############....',
    '....#++######++#....',
    '....############....',
    '...##############...',
    '..################..',
    '####################',
    '##++############++##',
    '##++############++##',
    '####################',
    '..################..',
    '...##############...',
    '....############....',
    '....####....####....',
    '....####....####....',
    '...#####....#####...',
    '....................',
  ],
};

/** SIEGE. Two shoulder blocks stand clear of the hull, notching the profile. */
const armouredSiege: GlyphDef = {
  size: 22,
  rows: [
    '......................',
    '.......########.......',
    '......##########......',
    '......#++####++#......',
    '......##########......',
    '####..##########..####',
    '####..##########..####',
    '######################',
    '######################',
    '######################',
    '##++##############++##',
    '##++##############++##',
    '######################',
    '.####################.',
    '..##################..',
    '...################...',
    '....##############....',
    '....#####....#####....',
    '....#####....#####....',
    '...######....######...',
    '...######....######...',
    '......................',
  ],
};

/**
 * CITADEL. 1800hp, armour 24, and a battlemented crown.
 *
 * Three merlons of three pixels rather than a finer comb: at 24 art px the quad
 * lands at 14 CSS px on a 360-wide phone, so one art pixel is 0.54 CSS px and a
 * two-pixel notch would be one screen pixel — present in the file and absent on
 * the glass. Three is the narrowest gap that still cuts the top edge visibly,
 * and the size alone does most of the work regardless.
 */
const armouredCitadel: GlyphDef = {
  size: 24,
  rows: [
    '........................',
    '...###....###....###....',
    '...###....###....###....',
    '..####################..',
    '..####################..',
    '..###++##########++###..',
    '..###++##########++###..',
    '..####################..',
    '.######################.',
    '########################',
    '########################',
    '##++################++##',
    '##++################++##',
    '########################',
    '########################',
    '.######################.',
    '..####################..',
    '..####################..',
    '...##################...',
    '....######....######....',
    '....######....######....',
    '...#######....#######...',
    '...#######....#######...',
    '........................',
  ],
};

/** Serpentine. Head high, body undulating down and left to a tapered tail. */
const weaver: GlyphDef = {
  size: 16,
  rows: [
    '................',
    '.......####.....',
    '......######....',
    '......#+##+#....',
    '......######....',
    '.......####.....',
    '.......####.....',
    '........####....',
    '.........####...',
    '........####....',
    '......####......',
    '....####........',
    '..####..........',
    '.####...........',
    '..###...........',
    '....##..........',
  ],
};

/*
 * ── weaver tiers ──
 *
 * Axis: **the tail, and then the head**. `phantom` forks the tail in two;
 * `mirage` puts a second bend in the body, so the S becomes a full wave;
 * `eclipse` — the only weaver carrying a shield, 260 of it — rings its head in
 * a hard halo, which is the same visual promise `shielded` makes and is meant
 * to be.
 */

/**
 * PHANTOM. One head, two tails, splayed to the full width of the cell.
 *
 * The first draft forked into a 2px stub tucked against the body, and the
 * magnified panel on `tools/creatures.html` showed exactly what that is worth:
 * nothing. Phantom shares the base weaver's radius of 5, so it gets no help
 * from size, and the fork is the whole tier marker — it has to be the bottom
 * half of the outline, not a detail inside it.
 */
const weaverPhantom: GlyphDef = {
  size: 16,
  rows: [
    '................',
    '.......####.....',
    '......######....',
    '......#+##+#....',
    '......######....',
    '.......####.....',
    '.......####.....',
    '........####....',
    '.........####...',
    '........####....',
    '......####......',
    '.....##..##.....',
    '....##....##....',
    '...##......##...',
    '..##........##..',
    '.##..........##.',
  ],
};

/** MIRAGE. Two bends, not one: the S becomes a full wave. */
const weaverMirage: GlyphDef = {
  size: 18,
  rows: [
    '..................',
    '........####......',
    '.......######.....',
    '.......#+##+#.....',
    '.......######.....',
    '........####......',
    '.........####.....',
    '..........####....',
    '.........####.....',
    '.......####.......',
    '.....####.........',
    '...####...........',
    '.....####.........',
    '.......####.......',
    '.........####.....',
    '..........####....',
    '.........####.....',
    '.......###........',
  ],
};

/** ECLIPSE. 900hp behind 260 of shield, and it wears the shield as a halo. */
const weaverEclipse: GlyphDef = {
  size: 20,
  rows: [
    '....................',
    '.....##########.....',
    '....##........##....',
    '...##..######..##...',
    '...##..######..##...',
    '...##..#+##+#..##...',
    '...##..######..##...',
    '...##..######..##...',
    '....##........##....',
    '.....##########.....',
    '.........####.......',
    '........####........',
    '......####..........',
    '....####............',
    '..####..............',
    '.####...............',
    '.####...............',
    '..###...............',
    '....##..............',
    '....................',
  ],
};

/** Bulbous sac with a hot fission seam straight down the middle. */
const splitter: GlyphDef = {
  size: 16,
  rows: [
    '................',
    '.....######.....',
    '...####++####...',
    '..#####++#####..',
    '.######++######.',
    '.######++######.',
    '#######++#######',
    '#######++#######',
    '#######++#######',
    '.######++######.',
    '.######++######.',
    '..#####++#####..',
    '...####++####...',
    '....###++###....',
    '.....##++##.....',
    '......####......',
  ],
};

/*
 * ── splitter chain ──
 *
 * Axis: **size, and the number of seams**, because that is literally what the
 * archetype is. A splitter is a bomb that becomes two smaller bombs, and the
 * catalogue already carries the ladder in `radius` — 3, 4, 6, 8, 11 for mote,
 * minor, major, prime, leviathan. Drawing each generation at its own size is
 * the one place in this file where the art is just reporting the sim.
 *
 * The seam count says how many more times it will come apart: one seam on the
 * two terminal generations, two on major, three on prime. `leviathan` crosses
 * the seam on both axes, which is a different shape rather than a fourth stripe
 * — four vertical stripes at 24px would read as a texture, not a count.
 */

/** MOTE. Terminal generation, 11hp. A diamond, so it is not a small circle. */
const splitterMote: GlyphDef = {
  size: 12,
  rows: [
    '............',
    '.....##.....',
    '....####....',
    '...######...',
    '..###++###..',
    '.####++####.',
    '.####++####.',
    '..###++###..',
    '...######...',
    '....####....',
    '.....##.....',
    '............',
  ],
};

/** MINOR. One seam left, and round where the mote is pointed. */
const splitterMinor: GlyphDef = {
  size: 14,
  rows: [
    '..............',
    '.....####.....',
    '...########...',
    '..##########..',
    '.#####++#####.',
    '######++######',
    '######++######',
    '######++######',
    '######++######',
    '.#####++#####.',
    '..####++####..',
    '...########...',
    '.....####.....',
    '..............',
  ],
};

/** MAJOR. Two seams: two more generations under it. */
const splitterMajor: GlyphDef = {
  size: 18,
  rows: [
    '..................',
    '......######......',
    '....##########....',
    '...############...',
    '..###++####++###..',
    '.####++####++####.',
    '#####++####++#####',
    '#####++####++#####',
    '#####++####++#####',
    '#####++####++#####',
    '#####++####++#####',
    '#####++####++#####',
    '.####++####++####.',
    '..###++####++###..',
    '...############...',
    '....##########....',
    '......######......',
    '..................',
  ],
};

/** PRIME. Three seams, and the head of the chain. */
const splitterPrime: GlyphDef = {
  size: 22,
  rows: [
    '......................',
    '.......########.......',
    '.....############.....',
    '...################...',
    '..##++####++####++##..',
    '.###++####++####++###.',
    '####++####++####++####',
    '####++####++####++####',
    '####++####++####++####',
    '####++####++####++####',
    '####++####++####++####',
    '####++####++####++####',
    '####++####++####++####',
    '####++####++####++####',
    '####++####++####++####',
    '####++####++####++####',
    '####++####++####++####',
    '.###++####++####++###.',
    '..##++####++####++##..',
    '...################...',
    '.....############.....',
    '.......########.......',
  ],
};

/** LEVIATHAN. 1400hp. The seam crosses, which no other generation does. */
const splitterLeviathan: GlyphDef = {
  size: 24,
  rows: [
    '........................',
    '........########........',
    '.....##############.....',
    '...##################...',
    '..#########++#########..',
    '.##########++##########.',
    '###########++###########',
    '###########++###########',
    '###########++###########',
    '###########++###########',
    '###########++###########',
    '++++++++++++++++++++++++',
    '++++++++++++++++++++++++',
    '###########++###########',
    '###########++###########',
    '###########++###########',
    '###########++###########',
    '###########++###########',
    '.##########++##########.',
    '..#########++#########..',
    '...##################...',
    '.....##############.....',
    '........########........',
    '........................',
  ],
};

/** A figure standing inside a hard bracket. The overshield IS the silhouette. */
const shielded: GlyphDef = {
  size: 16,
  rows: [
    '................',
    '.####......####.',
    '.##..........##.',
    '.##...####...##.',
    '.##..######..##.',
    '.##..#+##+#..##.',
    '.##..######..##.',
    '.##...####...##.',
    '.##..######..##.',
    '.##.########.##.',
    '.##..######..##.',
    '.##...####...##.',
    '.##...##.##..##.',
    '.##..###.###.##.',
    '.##..........##.',
    '.####......####.',
  ],
};

/*
 * ── shielded tiers ──
 *
 * Axis: **how far round the figure the bracket goes**. The base is two bars
 * with a gap top and bottom; `warden` closes both ends into a rectangle;
 * `aegis` closes it into a hexagon with no corner left open. Shield values of
 * 60, 180 and 440 read directly off how much of the perimeter is drawn, which
 * is the only tier ladder in the roster that is also a literal description of
 * the mechanic.
 */

/** WARDEN. The bracket closes: a full rectangle, 180 of shield. */
const shieldedWarden: GlyphDef = {
  size: 20,
  rows: [
    '....................',
    '####################',
    '####################',
    '##................##',
    '##....########....##',
    '##...##########...##',
    '##...##+####+##...##',
    '##...##########...##',
    '##....########....##',
    '##...##########...##',
    '##..############..##',
    '##...##########...##',
    '##....########....##',
    '##....###..###....##',
    '##...###....###...##',
    '##...###....###...##',
    '##................##',
    '####################',
    '####################',
    '....................',
  ],
};

/** AEGIS. 440 of shell, drawn as a shell: a closed hexagon, no open corner. */
const shieldedAegis: GlyphDef = {
  size: 22,
  rows: [
    '......................',
    '.......########.......',
    '.....####....####.....',
    '...####........####...',
    '..###....####....###..',
    '..##....######....##..',
    '.##....##+##+##....##.',
    '.##....########....##.',
    '.##.....######.....##.',
    '.##....########....##.',
    '.##...##########...##.',
    '.##....########....##.',
    '.##.....######.....##.',
    '.##.....###..###...##.',
    '.##....###....###..##.',
    '.##................##.',
    '..##..............##..',
    '..###............###..',
    '...####........####...',
    '.....####....####.....',
    '.......########.......',
    '......................',
  ],
};

// ─────────────────────────────────────────────────────────── swarmling (6) ──
//
// A mite, drawn as a 1px ring around an authored dark core.
//
// This was a filled blob, and 460 filled blobs additively summed into one
// continuous field with no interior structure — measured, not a single body was
// countable in a 300x240 crop. Every Geometry Wars swarm body is a hollow ring
// for exactly this reason: overlap two rings and you still see two, because
// each one carries its own hole. Hollowing also cuts emitted area per sprite by
// about 60%, which takes the same load off the bloom pyramid.
//
// Eight hand-drawn facings rather than a rotated sprite: an 8x8 shape resampled
// at 22.5° turns to mush, and eight is the honest ceiling — at this size the
// sixteen half-octants are not physically distinguishable in pixels. The `+`
// pair is the nose and it is the only thing that moves between facings, so the
// ring stays a stable shape while the heading still reads.

/**
 * The ring every facing shares. `+` marks the nose, placed per direction.
 *
 * Six pixels across, not eight. The ring was the right idea and it was drawn
 * too big to work: measured nearest-neighbour spacing inside a dense knot is
 * 4.97-5.49 buffer pixels, so an 8px body plus its 1px dark rim overlapped its
 * neighbour's *core* and the holes closed anyway — 76% ink against Geometry
 * Wars' 53%. At 6px the body plus rim is 8px against a 5px spacing, so a
 * neighbour's dark rim lands on the ring rather than through the middle of it
 * and each body keeps its own hole. Shape was never the problem; the ratio of
 * body to spacing was.
 */
const swarmlingE: GlyphDef = {
  size: 6,
  rows: [
    '.####.',
    '#----#',
    '#---+#',
    '#---+#',
    '#----#',
    '.####.',
  ],
};

const swarmlingSE: GlyphDef = {
  size: 6,
  rows: [
    '.####.',
    '#----#',
    '#----#',
    '#---+#',
    '#--+-#',
    '.####.',
  ],
};

const swarmlingS: GlyphDef = {
  size: 6,
  rows: [
    '.####.',
    '#----#',
    '#----#',
    '#----#',
    '#-++-#',
    '.####.',
  ],
};

const swarmlingSW: GlyphDef = {
  size: 6,
  rows: [
    '.####.',
    '#----#',
    '#----#',
    '#+---#',
    '#-+--#',
    '.####.',
  ],
};

const swarmlingW: GlyphDef = {
  size: 6,
  rows: [
    '.####.',
    '#----#',
    '#+---#',
    '#+---#',
    '#----#',
    '.####.',
  ],
};

const swarmlingNW: GlyphDef = {
  size: 6,
  rows: [
    '.####.',
    '#-+--#',
    '#+---#',
    '#----#',
    '#----#',
    '.####.',
  ],
};

const swarmlingN: GlyphDef = {
  size: 6,
  rows: [
    '.####.',
    '#-++-#',
    '#----#',
    '#----#',
    '#----#',
    '.####.',
  ],
};

const swarmlingNE: GlyphDef = {
  size: 6,
  rows: [
    '.####.',
    '#--+-#',
    '#---+#',
    '#----#',
    '#----#',
    '.####.',
  ],
};

/*
 * ── swarmling tiers ──
 *
 * Axis: **ring diameter**, and nothing else, because nothing else survives.
 *
 * A swarmling draws as an 8px quad, which is 4.30 CSS px on a 360-wide phone.
 * At that size there is no silhouette to speak of — a fin, a notch or a spike
 * is a fraction of one screen pixel and the bloom eats it before the eye gets
 * near. What does survive is area: a 6px quad against a 10px one is a 1.7x
 * linear difference and it reads at a glance in a knot of forty bodies, which
 * is the only read this family can actually deliver.
 *
 * So `mite` drops to 4 and `drone` grows to 8, against the base 6 — a ladder
 * matching the catalogue's radii of 2, 3 and 4. Each keeps all eight facings,
 * because the facings are what make a swarm countable rather than one field of
 * light, and losing them to buy a tier read would be trading the bigger problem
 * for the smaller one.
 *
 * `hornet` is the honest gap. It shares the base's radius of 3, so there is no
 * diameter left to give it; it gets a lozenge instead of a square ring, which
 * separates it under a magnifier and does not separate it in play. See the
 * report — the player reads hornet off its speed, which is the fastest thing in
 * the family by 40%.
 */

// mite — 4px ring. The interior is 2x2, so the nose is one cell of it.
const miteE: GlyphDef = { size: 4, rows: ['.##.', '#-+#', '#-+#', '.##.'] };
const miteSE: GlyphDef = { size: 4, rows: ['.##.', '#--#', '#-+#', '.##.'] };
const miteS: GlyphDef = { size: 4, rows: ['.##.', '#--#', '#++#', '.##.'] };
const miteSW: GlyphDef = { size: 4, rows: ['.##.', '#--#', '#+-#', '.##.'] };
const miteW: GlyphDef = { size: 4, rows: ['.##.', '#+-#', '#+-#', '.##.'] };
const miteNW: GlyphDef = { size: 4, rows: ['.##.', '#+-#', '#--#', '.##.'] };
const miteN: GlyphDef = { size: 4, rows: ['.##.', '#++#', '#--#', '.##.'] };
const miteNE: GlyphDef = { size: 4, rows: ['.##.', '#-+#', '#--#', '.##.'] };

// hornet — 6px lozenge. Same footprint as the base ring, pointed rather than
// square. Reads on this sheet; does not read at 4.3 CSS px.
const hornetE: GlyphDef = {
  size: 6,
  rows: ['..##..', '.#--#.', '#---+#', '#---+#', '.#--#.', '..##..'],
};
const hornetSE: GlyphDef = {
  size: 6,
  rows: ['..##..', '.#--#.', '#----#', '#---+#', '.#-+#.', '..##..'],
};
const hornetS: GlyphDef = {
  size: 6,
  rows: ['..##..', '.#--#.', '#----#', '#----#', '.#++#.', '..##..'],
};
const hornetSW: GlyphDef = {
  size: 6,
  rows: ['..##..', '.#--#.', '#----#', '#+---#', '.#+-#.', '..##..'],
};
const hornetW: GlyphDef = {
  size: 6,
  rows: ['..##..', '.#--#.', '#+---#', '#+---#', '.#--#.', '..##..'],
};
const hornetNW: GlyphDef = {
  size: 6,
  rows: ['..##..', '.#+-#.', '#+---#', '#----#', '.#--#.', '..##..'],
};
const hornetN: GlyphDef = {
  size: 6,
  rows: ['..##..', '.#++#.', '#----#', '#----#', '.#--#.', '..##..'],
};
const hornetNE: GlyphDef = {
  size: 6,
  rows: ['..##..', '.#-+#.', '#---+#', '#----#', '.#--#.', '..##..'],
};

// drone — 8px ring. 34hp and armour 3, the heaviest thing that still flocks.
const droneE: GlyphDef = {
  size: 8,
  rows: [
    '..####..', '.#----#.', '#------#', '#-----+#',
    '#-----+#', '#------#', '.#----#.', '..####..',
  ],
};
const droneSE: GlyphDef = {
  size: 8,
  rows: [
    '..####..', '.#----#.', '#------#', '#------#',
    '#----+-#', '#-----+#', '.#----#.', '..####..',
  ],
};
const droneS: GlyphDef = {
  size: 8,
  rows: [
    '..####..', '.#----#.', '#------#', '#------#',
    '#------#', '#--++--#', '.#----#.', '..####..',
  ],
};
const droneSW: GlyphDef = {
  size: 8,
  rows: [
    '..####..', '.#----#.', '#------#', '#------#',
    '#-+----#', '#+-----#', '.#----#.', '..####..',
  ],
};
const droneW: GlyphDef = {
  size: 8,
  rows: [
    '..####..', '.#----#.', '#------#', '#+-----#',
    '#+-----#', '#------#', '.#----#.', '..####..',
  ],
};
const droneNW: GlyphDef = {
  size: 8,
  rows: [
    '..####..', '.#----#.', '#+-----#', '#-+----#',
    '#------#', '#------#', '.#----#.', '..####..',
  ],
};
const droneN: GlyphDef = {
  size: 8,
  rows: [
    '..####..', '.#----#.', '#--++--#', '#------#',
    '#------#', '#------#', '.#----#.', '..####..',
  ],
};
const droneNE: GlyphDef = {
  size: 8,
  rows: [
    '..####..', '.#----#.', '#-----+#', '#----+-#',
    '#------#', '#------#', '.#----#.', '..####..',
  ],
};

// ──────────────────────────────────────────────────────────────── boss (32) ──

/**
 * Horned colossus. Crown of four horns, a visor with two burning slots, slab
 * pauldrons, a reactor core in the chest, and clawed hands. Dense enough that
 * at 32px it still reads as one mass rather than a pile of parts.
 */
const boss: GlyphDef = {
  size: 32,
  rows: [
    '..##........................##..',
    '..###......................###..',
    '...####..................####...',
    '....#####..............#####....',
    '.....#####............#####.....',
    '......########....########......',
    '.......##################.......',
    '........################........',
    '........##+++######+++##........',
    '........################........',
    '.........##############.........',
    '..........############..........',
    '...........##########...........',
    '..######...##########...######..',
    '.########..##########..########.',
    '.########..##########..########.',
    '.########..##########..########.',
    '..######..#####++#####..######..',
    '..######..####++++####..######..',
    '...####...###++++++###...####...',
    '...####..####++++++####..####...',
    '...####..#####++++#####..####...',
    '...####..######++######..####...',
    '...####..##############..####...',
    '..#####...############...#####..',
    '..#.#.#....##########....#.#.#..',
    '...........##########...........',
    '..........####....####..........',
    '.........#####....#####.........',
    '.........#####....#####.........',
    '........######....######........',
    '.......#######....#######.......',
  ],
};

// ─────────────────────────────────────────────────────────── world marks ──

/** Spawn gate: a heavy ring with a hot core burning through it. */
const portalIn: GlyphDef = {
  size: 16,
  rows: [
    '....########....',
    '..############..',
    '.####......####.',
    '.##..........##.',
    '###....##....###',
    '##....####....##',
    '##...######...##',
    '##..++####++..##',
    '##..++####++..##',
    '##...######...##',
    '##....####....##',
    '###....##....###',
    '.##..........##.',
    '.####......####.',
    '..############..',
    '....########....',
  ],
};

/** Exit gate: the same ring, hollowed out. Nothing comes back through it. */
const portalOut: GlyphDef = {
  size: 16,
  rows: [
    '....########....',
    '..############..',
    '.####......####.',
    '.##..........##.',
    '###..........###',
    '##....####....##',
    '##...##..##...##',
    '##..##....##..##',
    '##..##....##..##',
    '##...##..##...##',
    '##....####....##',
    '###..........###',
    '.##..........##.',
    '.####......####.',
    '..############..',
    '....########....',
  ],
};

/** What is left after a kill: a flattened smear with embers coming off it. */
const corpse: GlyphDef = {
  size: 16,
  rows: [
    '................',
    '................',
    '................',
    '................',
    '.....+....+.....',
    '...+..........+.',
    '................',
    '....##....##....',
    '..############..',
    '.##############.',
    '..############..',
    '...####..####...',
    '..+..#....#..+..',
    '.+..........+...',
    '................',
    '................',
  ],
};

/** Leak marker and boss-round warning. Cranium, sockets, teeth. */
const skull: GlyphDef = {
  size: 16,
  rows: [
    '................',
    '....########....',
    '..############..',
    '.##############.',
    '.##############.',
    '.##...####...##.',
    '.##...####...##.',
    '.####..##..####.',
    '.##############.',
    '..#####..#####..',
    '..############..',
    '...##########...',
    '...#.#.##.#.#...',
    '...##########...',
    '....########....',
    '................',
  ],
};

// ───────────────────────────────────────────────────── status badges (8) ──

/** Slowed: a six-arm ice fleck. */
const slowed: GlyphDef = {
  size: 8,
  rows: [
    '...##...',
    '.#.##.#.',
    '..####..',
    '########',
    '########',
    '..####..',
    '.#.##.#.',
    '...##...',
  ],
};

/** Burning: a teardrop flame with a hot centre. */
const burning: GlyphDef = {
  size: 8,
  rows: [
    '...#....',
    '...##...',
    '..####..',
    '..#+##..',
    '.##++##.',
    '.#++++#.',
    '.##++##.',
    '..####..',
  ],
};

/** Corroded: a fat acid drip under two smaller ones. */
const corroded: GlyphDef = {
  size: 8,
  rows: [
    '.#....#.',
    '.##..##.',
    '..#..#..',
    '...##...',
    '..####..',
    '.##++##.',
    '.#++++#.',
    '..####..',
  ],
};

/** Stunned: three sparks circling the head. */
const stunned: GlyphDef = {
  size: 8,
  rows: [
    '..#...#.',
    '.###.###',
    '..#...#.',
    '........',
    '...#....',
    '..###...',
    '...#....',
    '........',
  ],
};

// ──────────────────────────────────────────────────────────────── exports ──

export const GLYPHS = {
  grunt,
  gruntHeavy,
  gruntElite,
  gruntMonolith,
  runner,
  runnerBlink,
  runnerCourier,
  runnerVector,
  armoured,
  armouredBulwark,
  armouredSiege,
  armouredCitadel,
  weaver,
  weaverPhantom,
  weaverMirage,
  weaverEclipse,
  splitter,
  splitterMote,
  splitterMinor,
  splitterMajor,
  splitterPrime,
  splitterLeviathan,
  shielded,
  shieldedWarden,
  shieldedAegis,
  boss,
  swarmlingE,
  swarmlingSE,
  swarmlingS,
  swarmlingSW,
  swarmlingW,
  swarmlingNW,
  swarmlingN,
  swarmlingNE,
  miteE,
  miteSE,
  miteS,
  miteSW,
  miteW,
  miteNW,
  miteN,
  miteNE,
  hornetE,
  hornetSE,
  hornetS,
  hornetSW,
  hornetW,
  hornetNW,
  hornetN,
  hornetNE,
  droneE,
  droneSE,
  droneS,
  droneSW,
  droneW,
  droneNW,
  droneN,
  droneNE,
  portalIn,
  portalOut,
  corpse,
  skull,
  slowed,
  burning,
  corroded,
  stunned,
} as const satisfies Record<string, GlyphDef>;

export type GlyphName = keyof typeof GLYPHS;

export const GLYPH_NAMES = Object.keys(GLYPHS) as GlyphName[];

/**
 * Facing variants, indexed by heading octant, counter-clockwise from east in
 * buffer space (y grows downward, so index 1 is south-east).
 *
 * Four gave at most four silhouettes across 460 bodies, which is a large part
 * of why the swarm read as one mass. Eight is the ceiling the pixel grid
 * actually supports at this size.
 */
export const SWARMLING_FACINGS: readonly GlyphName[] = [
  'swarmlingE',
  'swarmlingSE',
  'swarmlingS',
  'swarmlingSW',
  'swarmlingW',
  'swarmlingNW',
  'swarmlingN',
  'swarmlingNE',
];

/**
 * One facing set per swarmling tier, keyed by the archetype's `glyph` field.
 *
 * A flocking body needs both a heading and a tier, and a single `glyph: string`
 * cannot carry both. So the catalogue names the *set* — `swarmling`,
 * `swarmlingMite`, `swarmlingHornet`, `swarmlingDrone` — and `EnemyRenderer`
 * picks the facing out of it. Anything not in here falls back to the base set,
 * so a new swarmling archetype renders rather than throwing.
 */
export const SWARMLING_TIER_FACINGS: Record<string, readonly GlyphName[]> = {
  swarmling: SWARMLING_FACINGS,
  swarmlingMite: ['miteE', 'miteSE', 'miteS', 'miteSW', 'miteW', 'miteNW', 'miteN', 'miteNE'],
  swarmlingHornet: [
    'hornetE', 'hornetSE', 'hornetS', 'hornetSW',
    'hornetW', 'hornetNW', 'hornetN', 'hornetNE',
  ],
  swarmlingDrone: [
    'droneE', 'droneSE', 'droneS', 'droneSW',
    'droneW', 'droneNW', 'droneN', 'droneNE',
  ],
};

/** Default art for each family. `EnemyCatalog` may override per archetype. */
export const GLYPH_FOR_FAMILY: Record<EnemyFamily, GlyphName> = {
  grunt: 'grunt',
  runner: 'runner',
  armoured: 'armoured',
  swarmling: 'swarmlingE',
  weaver: 'weaver',
  splitter: 'splitter',
  shielded: 'shielded',
  boss: 'boss',
};

/** Badge glyphs, in the order they are offered when several apply at once. */
export const STATUS_GLYPHS: readonly GlyphName[] = [
  'stunned',
  'burning',
  'corroded',
  'slowed',
];

/** Bit positions matching `STATUS_GLYPHS`. Packed into one instance float. */
export const STATUS_BIT = {
  stunned: 1,
  burning: 2,
  corroded: 4,
  slowed: 8,
} as const;

export function isGlyphName(name: string): name is GlyphName {
  return Object.prototype.hasOwnProperty.call(GLYPHS, name);
}

/**
 * Throws on malformed art. Cheap, and it turns a typo in a row string into a
 * loud boot failure instead of a silently sheared sprite.
 */
export function validateGlyphs(): void {
  for (let i = 0; i < GLYPH_NAMES.length; i++) {
    const name = GLYPH_NAMES[i];
    const g = GLYPHS[name];
    if (g.rows.length !== g.size) {
      throw new Error(`glyph "${name}": ${g.rows.length} rows, expected ${g.size}`);
    }
    for (let y = 0; y < g.rows.length; y++) {
      const row = g.rows[y];
      if (row.length !== g.size) {
        throw new Error(`glyph "${name}" row ${y}: ${row.length} chars, expected ${g.size}`);
      }
      for (let x = 0; x < row.length; x++) {
        const c = row[x];
        if (c !== '.' && c !== '#' && c !== '+' && c !== '-') {
          throw new Error(`glyph "${name}" row ${y} col ${x}: bad char "${c}"`);
        }
      }
    }
  }
}
